<template>
  <div class="mollie-manage">
    <div v-if="loading" class="mollie-manage__loading">
      <LoadingSpinner />
    </div>

    <template v-else-if="!status?.connected">
      <div class="mollie-manage__not-connected">
        <p>{{ $t('mollie.reasons.not_connected') }}</p>
        <Btn variant="primary" :loading="connecting" @click="handleConnect">
          {{ $t('mollie.actions.connect') }}
        </Btn>
      </div>
    </template>

    <template v-else>
      <div
        v-if="needsReauth"
        class="mollie-manage__banner mollie-manage__banner--error"
      >
        <BaseIcon name="exclamation-circle" size="14" />
        <span>{{ $t('mollie.reasons.reauth_required') }}</span>
      </div>

      <div
        v-else-if="!onboardingComplete"
        class="mollie-manage__banner mollie-manage__banner--warning"
      >
        <BaseIcon name="information-circle" size="14" />
        <div>
          <strong>{{ $t('mollie.status.onboarding_incomplete') }}</strong>
          <p>{{ $t('mollie.status.onboarding_incomplete_description') }}</p>
          <a
            href="https://my.mollie.com/"
            target="_blank"
            rel="noopener noreferrer"
            class="mollie-manage__link"
          >
            {{ $t('mollie.status.onboarding_open_link') }}
            <BaseIcon name="external-link" size="12" />
          </a>
        </div>
      </div>

      <div
        v-else-if="!canAcceptLivePayments"
        class="mollie-manage__banner mollie-manage__banner--warning"
      >
        <BaseIcon name="information-circle" size="14" />
        <div>
          <strong>{{ $t('mollie.status.kyc_pending') }}</strong>
          <p>{{ $t('mollie.status.kyc_pending_description') }}</p>
        </div>
      </div>

      <dl class="mollie-manage__details">
        <div class="mollie-manage__row">
          <dt>{{ $t('mollie.status.organization') }}</dt>
          <dd>{{ status?.mollie_organization?.name || status?.profile_name || '—' }}</dd>
        </div>
        <div v-if="status?.profile_name" class="mollie-manage__row">
          <dt>{{ $t('mollie.status.profile') }}</dt>
          <dd>{{ status.profile_name }}</dd>
        </div>
        <div class="mollie-manage__row">
          <dt>{{ $t('mollie.status.mode') }}</dt>
          <dd>
            <span class="mollie-manage__mode" :class="`mollie-manage__mode--${status?.mode || 'live'}`">
              {{ status?.mode === 'test' ? $t('mollie.status.mode_test') : $t('mollie.status.mode_live') }}
            </span>
          </dd>
        </div>
        <div v-if="status?.connected_at" class="mollie-manage__row">
          <dt>{{ $t('mollie.status.connected_at') }}</dt>
          <dd>{{ formatDateLong(status.connected_at) }}</dd>
        </div>
        <div class="mollie-manage__row">
          <dt>{{ $t('mollie.status.can_accept_live_payments') }}</dt>
          <dd>
            <BaseIcon
              :name="canAcceptLivePayments ? 'check-circle' : 'circle-dot'"
              size="14"
              :class="canAcceptLivePayments ? 'mollie-manage__icon-ok' : 'mollie-manage__icon-pending'"
            />
          </dd>
        </div>
      </dl>

      <div class="mollie-manage__actions">
        <Btn
          v-if="needsReauth"
          variant="primary"
          icon="redo"
          :loading="connecting"
          @click="handleConnect"
        >
          {{ $t('mollie.actions.reconnect') }}
        </Btn>
        <Btn
          v-else
          variant="outlined"
          :loading="refreshing"
          @click="handleRefresh"
        >
          {{ $t('mollie.actions.refresh') }}
        </Btn>

        <Btn
          variant="danger"
          :loading="disconnecting"
          @click="handleDisconnect"
        >
          {{ $t('mollie.actions.disconnect') }}
        </Btn>
      </div>
    </template>
  </div>
</template>

<script>
  import { computed, getCurrentInstance, onMounted, ref } from 'vue';
  import dayjs from 'dayjs';

  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';

  import { useMollieStore } from '@/store/mollieStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { useInvoiceStore } from '@/store/invoiceStore';

  export default {
    name: 'MollieManageModal',

    components: { Btn, BaseIcon, LoadingSpinner },

    props: {
      modalId: { type: [String, Number], default: null },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const mollieStore = useMollieStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();
      const invoiceStore = useInvoiceStore();

      const loading = ref(false);
      const connecting = ref(false);
      const refreshing = ref(false);
      const disconnecting = ref(false);

      const status = computed(() => mollieStore.status);
      const needsReauth = computed(() => mollieStore.needsReauth);
      const onboardingComplete = computed(() => mollieStore.onboardingComplete);
      const canAcceptLivePayments = computed(() => mollieStore.canAcceptLivePayments);

      const refreshAll = async () => {
        await Promise.all([
          mollieStore.fetchStatus({ force: true }),
          invoiceStore.fetchTriggerAvailability(true),
        ]);
      };

      const formatDateLong = (date) =>
        date ? dayjs(date).format('DD-MM-YYYY HH:mm') : '';

      const handleConnect = async () => {
        connecting.value = true;
        try {
          const result = await mollieStore.startOAuth();
          if (result?.success) {
            await refreshAll();
            toastStore.addToast({ type: 'success', message: $t('mollie.connect.success') });
          } else if (result?.error === 'popup_closed') {
            toastStore.addToast({ type: 'info', message: $t('mollie.connect.cancelled') });
          } else if (result?.error === 'popup_blocked') {
            toastStore.addToast({ type: 'warning', message: $t('mollie.connect.popup_blocked') });
          } else {
            toastStore.addToast({ type: 'error', message: $t('mollie.connect.failed') });
          }
        } catch (error) {
          const msg = error?.response?.data?.error || error.message || $t('mollie.connect.failed');
          toastStore.addToast({ type: 'error', message: msg });
        } finally {
          connecting.value = false;
        }
      };

      const handleRefresh = async () => {
        refreshing.value = true;
        try {
          await refreshAll();
        } catch (error) {
          const msg = error?.response?.data?.error || error.message;
          toastStore.addToast({ type: 'error', message: msg });
        } finally {
          refreshing.value = false;
        }
      };

      const handleDisconnect = () => {
        const confirmId = uiStore.addModal(null, {
          title: $t('mollie.disconnect.title'),
          description: $t('mollie.disconnect.description'),
          status: 'danger',
          submitText: $t('mollie.disconnect.confirm'),
          cancelText: $t('common.cancel'),
          onSubmit: async () => {
            disconnecting.value = true;
            try {
              await mollieStore.disconnect();
              await invoiceStore.fetchTriggerAvailability(true);
              uiStore.closeModal(confirmId);
              if (props.modalId) uiStore.closeModal(props.modalId);
              toastStore.addToast({
                type: 'success',
                message: $t('mollie.disconnect.success'),
              });
            } catch (error) {
              uiStore.closeModal(confirmId);
              const msg =
                error?.response?.data?.error || error.message || $t('mollie.disconnect.error');
              toastStore.addToast({ type: 'error', message: msg });
            } finally {
              disconnecting.value = false;
            }
          },
          onCancel: () => uiStore.closeModal(confirmId),
        });
      };

      onMounted(async () => {
        loading.value = true;
        try {
          await mollieStore.fetchStatus({ force: true });
        } catch {
          /* error already surfaced via toast on click */
        } finally {
          loading.value = false;
        }
      });

      return {
        loading,
        connecting,
        refreshing,
        disconnecting,
        status,
        needsReauth,
        onboardingComplete,
        canAcceptLivePayments,
        formatDateLong,
        handleConnect,
        handleRefresh,
        handleDisconnect,
      };
    },
  };
</script>

<style scoped lang="scss">
  .mollie-manage {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__loading {
      display: flex;
      justify-content: center;
      padding: 2rem 0;
    }

    &__not-connected {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      align-items: flex-start;

      p {
        margin: 0;
        font-size: 0.875rem;
        color: var(--slate-600);
      }
    }

    &__banner {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      font-size: 0.875rem;
      line-height: 1.45;

      strong {
        display: block;
        font-weight: 600;
      }

      p {
        margin: 0.25rem 0 0;
      }

      &--warning {
        background: var(--amber-50);
        color: var(--amber-800);
      }

      &--error {
        background: var(--red-50);
        color: var(--red-700);
      }
    }

    &__link {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      margin-top: 0.5rem;
      font-weight: 600;
      color: inherit;
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    &__details {
      margin: 0;
      display: grid;
      grid-template-columns: 1fr;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      background: var(--slate-50);
      border-radius: 0.5rem;
    }

    &__row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      font-size: 0.875rem;

      dt {
        color: var(--slate-600);
      }

      dd {
        margin: 0;
        color: var(--slate-900);
        font-weight: 500;
      }
    }

    &__mode {
      display: inline-flex;
      align-items: center;
      padding: 0.125rem 0.5rem;
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0.05em;
      border-radius: 0.25rem;

      &--live {
        background: var(--green-100);
        color: var(--green-700);
      }

      &--test {
        background: var(--amber-100);
        color: var(--amber-800);
      }
    }

    &__icon-ok {
      color: var(--green-600);
    }

    &__icon-pending {
      color: var(--amber-600);
    }

    &__actions {
      display: flex;
      justify-content: space-between;
      gap: 0.5rem;
      flex-wrap: wrap;
    }
  }
</style>
